/**
 * @file     startListing.ts
 * @purpose  Starts an owner listing from the front door: create the draft
 *           for the kind, then save what the estimate already knows (the
 *           address, bedrooms and home type) so the builder opens with it.
 *           A draft that starts but cannot take the address still opens.
 */
'use client';

import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { listingApi } from '../api';
import type { AddressParts } from '../steps/AddressStep';

export const ESTIMATE_MEMORY_KEY = 'relta-owner-estimate';

export type EstimateMemory = { query: string; address: AddressParts | null; bedrooms: number; homeType: string };

export async function startListing(input: {
  kind: string;
  address: AddressParts | null;
  attributes: Record<string, unknown>;
  api?: Pick<typeof listingApi, 'createDraft' | 'saveDraft'>;
}): Promise<string> {
  const api = input.api ?? listingApi;
  const { listing } = await api.createDraft(input.kind);
  const patch = {
    ...(input.address ? { address: input.address } : {}),
    ...(Object.keys(input.attributes).length ? { attributes: input.attributes } : {}),
  };
  if (Object.keys(patch).length) {
    await api.saveDraft(listing.id, patch).catch((err: unknown) => {
      reportSilentFailure(err, { area: 'owner-listings/start-listing', tags: { listingId: listing.id } });
    });
  }
  return listing.id;
}

export function builderHref(id: string, from: string): string {
  return `/dashboard/listings/${encodeURIComponent(id)}/edit?from=${encodeURIComponent(from)}`;
}

/** Per-viewer convenience only: the estimate survives the sign-up round trip. */
export function readEstimateMemory(): EstimateMemory | null {
  try {
    const raw = window.localStorage.getItem(ESTIMATE_MEMORY_KEY);
    return raw ? (JSON.parse(raw) as EstimateMemory) : null;
  } catch {
    return null;
  }
}

export function writeEstimateMemory(value: EstimateMemory | null): void {
  try {
    if (value) window.localStorage.setItem(ESTIMATE_MEMORY_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(ESTIMATE_MEMORY_KEY);
  } catch {
    // Private mode or blocked storage: the visitor re-enters the address.
  }
}
